import { useForm } from "react-hook-form";
import { Box } from "@mui/material";
import FormRow from "components/form/FormRow";
import { MESSAGE, REGEX } from "constants/validate";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import CancelButton from "components/button/CancelButton";
import ButtonComponent from "components/button/ButtonComponent";
import { formatDateTime } from "utils";
import { useNavigate } from "react-router";
import { USER_TYPE, USER_TYPE_LIST } from "constants/user-type";
import { useEffect, useMemo } from "react";
import { DASH_FORMAT_DATE } from "constants/format";
import dayjs from "dayjs";
import { IUpsertUserRequest, TOption, User } from "../types/user";

const styles = {
  field: {
    width: "100%",
    height: "48px",
    boxSizing: "border-box",
    backgroundColor: "var(--white)",
    borderRadius: 0,
    border: "3px solid var(--silver-150)",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  closeBtn: {},
};

interface IUserFormProps {
  type: "create" | "update";
  visible: boolean;
  onClose: () => void;
  onHandleSubmit: (form: IUpsertUserRequest) => void;
  isLoading: boolean;
  getSuggestCode: (userType: string) => Promise<void>;
  suggestCodeList: TOption[];
  userDetail?: User;
}

const REACT_APP_DEFAULT_EXPIRATION_PERIOD: number = Number(
  process.env.REACT_APP_DEFAULT_EXPIRATION_PERIOD ?? 1,
);

/**
 * UserFormModal Component
 *
 * This component renders a modal form for creating or updating user details.
 * It uses `react-hook-form` for form management and validation.
 * @param {IUserFormProps} props - The props for the component.
 * @param {"create" | "update"} props.type - Specifies whether the form is for creating or updating a user.
 * @param {boolean} props.visible - Controls the visibility of the modal.
 * @param {() => void} props.onClose - Callback function to close the modal.
 * @param {(form: IUpsertUserRequest) => void} props.onHandleSubmit - Callback function to handle form submission.
 * @param {boolean} props.isLoading - Indicates whether the form is in a loading state.
 * @param {(userType: string) => Promise<void>} props.getSuggestCode - Function to fetch suggested codes based on user type.
 * @param {TOption[]} props.suggestCodeList - List of options for the identification code field.
 * @param {User} [props.userDetail] - User details for pre-filling the form in update mode.
 */
export default function UserFormModal({
  type = "create",
  visible,
  onClose,
  onHandleSubmit,
  isLoading,
  getSuggestCode,
  suggestCodeList,
  userDetail,
}: IUserFormProps) {
  const navigate = useNavigate();
  const EXPIRATION = dayjs().add(REACT_APP_DEFAULT_EXPIRATION_PERIOD, "month");

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<IUpsertUserRequest>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      id: undefined,
      user_type: "",
      identification_cd: "",
      user_name: "",
      email_address: "",
      password: "",
      exp_date: EXPIRATION.format(DASH_FORMAT_DATE),
      status_flag: 1,
      delete_flag: 0,
    },
  });

  const isUpdate = type === "update";
  const watchUserType = watch("user_type");
  const isHeadquarters = watchUserType === USER_TYPE_LIST.headquarters.value;
  const allowTyping = useMemo(
    () =>
      watchUserType === USER_TYPE_LIST.headquarters.value ||
      watchUserType === USER_TYPE_LIST.joyful.value,
    [watchUserType],
  );

  const onSubmit = (data: IUpsertUserRequest) => {
    if (isLoading || Object.keys(errors).length > 0) return;
    const requestData: IUpsertUserRequest = {
      ...data,
      identification_cd:
        typeof data.identification_cd === "string"
          ? data.identification_cd
          : (data.identification_cd as TOption).value.toString(),
    };
    onHandleSubmit(requestData);
  };

  const USER_TYPE_OPTIONS = useMemo(
    () =>
      Object.keys(USER_TYPE).map((key) => ({
        label: USER_TYPE[key as keyof typeof USER_TYPE],
        value: key,
      })),
    [],
  );

  useEffect(() => {
    if (userDetail && userDetail.id) {
      reset({
        id: userDetail.id,
        user_type: userDetail.user_type,
        identification_cd: userDetail.identification_cd,
        user_name: userDetail.user_name,
        email_address: userDetail.email_address,
        password: "",
        exp_date: dayjs(userDetail.exp_date).format(DASH_FORMAT_DATE),
        status_flag: userDetail.status_flag ? 1 : 0,
      });
    }
  }, [userDetail?.id]);

  useEffect(() => {
    if (watchUserType === USER_TYPE_LIST.headquarters.value) {
      setValue("identification_cd", "0", { shouldValidate: true });
    }
    if (watchUserType) {
      getSuggestCode(watchUserType);
    }
  }, [watchUserType]);

  return (
    <Modal
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      open={visible}
      onClose={onClose}
    >
      <Box
        sx={{
          maxHeight: "90vh",
          overflow: "auto",
          width: "80%",
          maxWidth: "870px",
          backgroundColor: "var(--white)",
          padding: { xs: 3, md: 4, xl: "30px 70px" },
          borderRadius: "21px",
          display: "flex",
          flexDirection: "column",
          rowGap: "20px",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="10px">
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: "fontWeightMedium",
              color: "var(--gray-700)",
              fontSize: { xs: 28, md: 33 },
            }}
          >
            {isUpdate ? "利用者詳細" : "利用者登録"}
            <Typography component="br" sx={{ display: { xs: "block", md: "none" } }} />
            {isUpdate && (
              <Typography
                component="span"
                sx={{
                  fontSize: { xs: 16, md: 21 },
                  fontWeight: "fontWeightMedium",
                  color: "var(--gray-700)",
                  ml: { xs: 0, md: 3 },
                }}
              >
                作成日 {formatDateTime(userDetail?.created_at || "")}
              </Typography>
            )}
          </Typography>
          <CancelButton onClose={onClose} />
        </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormRow
            sxLabel={{ pl: 4, width: "40%" }}
            label="利用者種別"
            sxBoxField={{ width: "60%", flexGrow: 1 }}
            sxField={{ ...styles.field }}
            name="user_type"
            control={control}
            type="select"
            rules={{ required: MESSAGE.EMPTY }}
            options={USER_TYPE_OPTIONS}
            onChange={(v) => setValue("identification_cd", v === USER_TYPE_LIST.headquarters.value ? "0" : "", { shouldValidate: true })}
          />

          <FormRow
            sxLabel={{ pl: 4, width: "40%" }}
            label="店舗/得意先/企業コード"
            sxBoxField={{ width: "60%", flexGrow: 1 }}
            sxField={{
              ...styles.field,
              display: "flex",
              alignItems: "center",
              "& .MuiOutlinedInput-root": {
                py: "5px",
                height: "100%",
                width: "100%",
              },
            }}
            name="identification_cd"
            control={control}
            type="autocomplete"
            autoCompleteAllowAny={allowTyping}
            rules={{ required: MESSAGE.EMPTY }}
            options={suggestCodeList}
            disabled={isHeadquarters}
          />

          <FormRow
            sxLabel={{ pl: 4, width: "40%" }}
            label="利用者名"
            sxBoxField={{ width: "60%", flexGrow: 1 }}
            sxField={{ ...styles.field }}
            name="user_name"
            control={control}
            rules={{
              required: MESSAGE.EMPTY,
              maxLength: {
                value: 100,
                message: MESSAGE.MAX_LENGTH(100),
              },
            }}
          />

          <FormRow
            sxLabel={{ pl: 4, width: "40%" }}
            label="メールアドレス"
            sxBoxField={{ width: "60%", flexGrow: 1 }}
            sxField={{ ...styles.field }}
            name="email_address"
            type="email"
            control={control}
            rules={{
              required: MESSAGE.EMPTY,
              pattern: {
                value: REGEX.EMAIL,
                message: MESSAGE.INVALID_EMAIL,
              },
              maxLength: {
                value: 320,
                message: MESSAGE.MAX_LENGTH(320),
              },
            }}
          />

          {!isUpdate && (
            <FormRow
              sxLabel={{ pl: 4, width: "40%" }}
              label="パスワード"
              sxBoxField={{ width: "60%", flexGrow: 1 }}
              sxField={{ ...styles.field }}
              name="password"
              type="password"
              control={control}
              rules={{
                required: MESSAGE.EMPTY_PASSWORD,
                pattern: {
                  value: REGEX.HALFWIDTH,
                  message: MESSAGE.VALIDATION_PASSWORD(8),
                },
                minLength: {
                  value: 8,
                  message: MESSAGE.VALIDATION_PASSWORD(8),
                },
              }}
            />
          )}

          <FormRow
            sxLabel={{ pl: 4, width: "40%" }}
            label="有効期限"
            sxBoxField={{ width: "60%", flexGrow: 1 }}
            sxField={{ ...styles.field }}
            name="exp_date"
            type="date"
            control={control}
            rules={{
              required: MESSAGE.EMPTY,
            }}
          />

          <FormRow
            sxLabel={{ pl: 4, width: "40%" }}
            label="有効/無効"
            sxBoxField={{ width: "60%", flexGrow: 1 }}
            sxField={{ ...styles.field }}
            name="status_flag"
            control={control}
            type="select"
            rules={{ required: MESSAGE.EMPTY }}
            options={[
              { label: "有効", value: 1 },
              { label: "無効", value: 0 },
            ]}
          />

          <Box display="flex" columnGap="15px" marginTop="25px" alignItems="center">
            <ButtonComponent
              disabled={isLoading || Object.keys(errors).length > 0}
              type="submit"
              label="登録"
              theme="dark"
              sx={{ height: "50px", minWidth: "185px", width: "28%" }}
            />
            <Typography
              sx={{ fontSize: { xs: 16, md: 21 } }}
              fontWeight="medium"
              color="var(--gray-700)"
            >
              ※本部のみ対応
            </Typography>
          </Box>
        </form>
        {isUpdate && (
          <>
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: 16, md: 21 },
                fontWeight: "medium",
                color: "var(--gray-600)",
              }}
            >
              更新日 {formatDateTime(userDetail?.updated_at || "")}
            </Typography>
            <Box
              display="flex"
              columnGap="15px"
              rowGap="10px"
              flexWrap="wrap"
              marginTop="5px"
              alignItems="center"
            >
              <ButtonComponent
                onClick={() => navigate(`/forgot-password?email=${userDetail?.email_address}`)}
                label="パスワード再設定"
                theme="dark"
                sx={{ height: "50px", minWidth: "185px", width: "28%" }}
              />
              <Typography
                fontWeight="medium"
                color="var(--gray-700)"
                sx={{ fontSize: { xs: 16, md: 21 } }}
              >
                ※パスワード再設定のメールが送信されます
              </Typography>
            </Box>
          </>
        )}
        <CancelButton sx={{ mt: 1.5 }} onClose={onClose} />
      </Box>
    </Modal>
  );
}
